/**
 * Owner: Masa Bayu
 * File: CommunityPeople.tsx
 * Date:Time: 2026-10-17 04:30
 * Description: Dedicated community people surfaces — author profile, followers/following,
 *   tag feed, mention feed, blocked, muted. Wired to the real QXCircle client API.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  blockQxCircleAuthor,
  followQxCircleAuthor,
  getQxCircleAuthorProfile,
  getQxCircleBlocks,
  getQxCircleMutes,
  getQxCircleMentionFeed,
  getQxCircleTagFeed,
  muteQxCircleAuthor,
  type QxCircleAuthorProfile,
  type QxCircleBlockedAuthor,
  type QxCircleFeedPost,
} from '../client/qxcircle-api';
import {
  getQxCircleFollowList,
  type QxCircleFollowListKind,
  type QxCircleFollowListPerson,
} from '../client/qxcircle-follow-list-api';
import { CommunityPostCard } from './CommunityPostCard';

export interface CommunityPeopleProps {
  readonly onNavigate: (path: string) => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return `${parts[0]?.[0] ?? '?'}${parts[1]?.[0] ?? ''}`;
}

export function CommunityProfile({
  handle,
  onNavigate,
}: CommunityPeopleProps & { readonly handle: string }) {
  const [profile, setProfile] = useState<QxCircleAuthorProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    getQxCircleAuthorProfile(handle)
      .then((next) => {
        if (alive) setProfile(next);
      })
      .catch(() => {
        if (alive) setProfile(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [handle]);

  useEffect(() => load(), [load]);

  if (loading) return <p className="ddx__status">Loading profile…</p>;
  if (profile === null) {
    return (
      <div className="ddx__empty">
        <h3>Profile not found</h3>
        <p>No author matches this handle.</p>
      </div>
    );
  }

  return (
    <div className="cmc">
      <header>
        <p className="ddx__eyebrow">Community · Author</p>
        <h2 className="ddx__title">{profile.authorName}</h2>
      </header>

      <div className="cmc__profile-head">
        <span className="cmc__avatar cmc__avatar--teal" aria-hidden="true">
          {initials(profile.authorName)}
        </span>
        <span className="cmc__who">
          <span className="cmc__name">@{profile.authorHandle}</span>
          <span className="cmc__handle">{profile.bio}</span>
          <span className="cmc__profile-stats">
            <span>
              <strong>{String(profile.followerCount)}</strong> followers
            </span>
            <span>
              <strong>{String(profile.followingCount)}</strong> following
            </span>
            <span>
              <strong>{String(profile.postCount)}</strong> posts
            </span>
          </span>
        </span>
      </div>

      <div className="ddx__actions">
        <button
          type="button"
          className="ddx__cta ddx__cta--solid"
          onClick={() => {
            void followQxCircleAuthor(profile.authorHandle).then(load);
          }}
        >
          {profile.followedByLocal ? 'Unfollow' : 'Follow'}
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            onNavigate(`/circle/profile/${encodeURIComponent(profile.authorHandle)}/followers`);
          }}
        >
          Followers
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            onNavigate(`/circle/profile/${encodeURIComponent(profile.authorHandle)}/following`);
          }}
        >
          Following
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            void muteQxCircleAuthor(profile.authorHandle).then(load);
          }}
        >
          {profile.mutedByLocal ? 'Unmute' : 'Mute'}
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            void blockQxCircleAuthor(profile.authorHandle).then(load);
          }}
        >
          {profile.blockedByLocal ? 'Unblock' : 'Block'}
        </button>
      </div>

      {profile.posts.length === 0 ? (
        <div className="ddx__empty">
          <h3>No posts yet</h3>
          <p>This author has not shared anything yet.</p>
        </div>
      ) : (
        <div className="cmc__feed">
          {profile.posts.map((post) => (
            <CommunityPostCard key={post.id} post={post} onNavigate={onNavigate} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

export function CommunityFollowList({
  handle,
  kind,
  onNavigate,
}: CommunityPeopleProps & { readonly handle: string; readonly kind: QxCircleFollowListKind }) {
  const [people, setPeople] = useState<readonly QxCircleFollowListPerson[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    getQxCircleFollowList(handle, kind)
      .then((response) => {
        if (alive) setPeople(response.items);
      })
      .catch(() => {
        if (alive) setPeople([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [handle, kind]);

  return (
    <div className="cmc">
      <header>
        <p className="ddx__eyebrow">Community · Network</p>
        <h2 className="ddx__title">{kind === 'followers' ? 'Followers' : 'Following'}</h2>
        <p className="ddx__lede">@{handle}</p>
      </header>
      {loading ? (
        <p className="ddx__status">Loading…</p>
      ) : people.length === 0 ? (
        <div className="ddx__empty">
          <h3>Nothing here yet</h3>
          <p>No {kind} to show for this author.</p>
        </div>
      ) : (
        <div className="cmc__list">
          {people.map((person) => (
            <div className="cmc__row" key={person.authorHandle}>
              <span className="cmc__avatar" aria-hidden="true">
                {initials(person.authorName)}
              </span>
              <span className="cmc__row-main">
                <span className="cmc__row-title">{person.authorName}</span>
                <span className="cmc__row-sub">@{person.authorHandle}</span>
              </span>
              <button
                type="button"
                className="cmc__action"
                onClick={() => {
                  onNavigate(`/circle/profile/${encodeURIComponent(person.authorHandle)}`);
                }}
              >
                View
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PostFeedSection({
  eyebrow,
  title,
  lede,
  posts,
  loading,
  onNavigate,
  onChanged,
}: {
  readonly eyebrow: string;
  readonly title: string;
  readonly lede: string;
  readonly posts: readonly QxCircleFeedPost[];
  readonly loading: boolean;
  readonly onNavigate: (path: string) => void;
  readonly onChanged: () => void;
}) {
  return (
    <div className="cmc">
      <header>
        <p className="ddx__eyebrow">{eyebrow}</p>
        <h2 className="ddx__title">{title}</h2>
        <p className="ddx__lede">{lede}</p>
      </header>
      {loading ? (
        <p className="ddx__status">Loading…</p>
      ) : posts.length === 0 ? (
        <div className="ddx__empty">
          <h3>No posts here</h3>
          <p>Nothing matches this feed yet.</p>
        </div>
      ) : (
        <div className="cmc__feed">
          {posts.map((post) => (
            <CommunityPostCard
              key={post.id}
              post={post}
              onNavigate={onNavigate}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function CommunityTagFeed({
  tag,
  onNavigate,
}: CommunityPeopleProps & { readonly tag: string }) {
  const [posts, setPosts] = useState<readonly QxCircleFeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    getQxCircleTagFeed(tag)
      .then((response) => {
        if (alive) setPosts(response.items);
      })
      .catch(() => {
        if (alive) setPosts([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [tag]);
  useEffect(() => load(), [load]);
  return (
    <PostFeedSection
      eyebrow="Community · Tag"
      title={`#${tag}`}
      lede="Posts using this tag."
      posts={posts}
      loading={loading}
      onNavigate={onNavigate}
      onChanged={load}
    />
  );
}

export function CommunityMentionFeed({
  handle,
  onNavigate,
}: CommunityPeopleProps & { readonly handle: string }) {
  const [posts, setPosts] = useState<readonly QxCircleFeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    getQxCircleMentionFeed(handle)
      .then((response) => {
        if (alive) setPosts(response.items);
      })
      .catch(() => {
        if (alive) setPosts([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [handle]);
  useEffect(() => load(), [load]);
  return (
    <PostFeedSection
      eyebrow="Community · Mentions"
      title={`@${handle}`}
      lede="Posts mentioning this handle."
      posts={posts}
      loading={loading}
      onNavigate={onNavigate}
      onChanged={load}
    />
  );
}

function AuthorListSection({
  title,
  lede,
  items,
  loading,
  onNavigate,
}: {
  readonly title: string;
  readonly lede: string;
  readonly items: readonly QxCircleBlockedAuthor[];
  readonly loading: boolean;
  readonly onNavigate: (path: string) => void;
}) {
  return (
    <div className="cmc">
      <header>
        <p className="ddx__eyebrow">Community · Safety</p>
        <h2 className="ddx__title">{title}</h2>
        <p className="ddx__lede">{lede}</p>
      </header>
      {loading ? (
        <p className="ddx__status">Loading…</p>
      ) : items.length === 0 ? (
        <div className="ddx__empty">
          <h3>None</h3>
          <p>No authors in this list.</p>
        </div>
      ) : (
        <div className="cmc__list">
          {items.map((item) => (
            <div className="cmc__row" key={item.authorHandle}>
              <span className="cmc__avatar" aria-hidden="true">
                {initials(item.authorName)}
              </span>
              <span className="cmc__row-main">
                <span className="cmc__row-title">{item.authorName}</span>
                <span className="cmc__row-sub">@{item.authorHandle}</span>
              </span>
              <button
                type="button"
                className="cmc__action"
                onClick={() => {
                  onNavigate(`/circle/profile/${encodeURIComponent(item.authorHandle)}`);
                }}
              >
                View
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CommunityBlocked({ onNavigate }: CommunityPeopleProps) {
  const [items, setItems] = useState<readonly QxCircleBlockedAuthor[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    getQxCircleBlocks()
      .then((response) => {
        if (alive) setItems(response.items);
      })
      .catch(() => {
        if (alive) setItems([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);
  return (
    <AuthorListSection
      title="Blocked"
      lede="Authors you have blocked. Their posts are hidden from your feeds."
      items={items}
      loading={loading}
      onNavigate={onNavigate}
    />
  );
}

export function CommunityMuted({ onNavigate }: CommunityPeopleProps) {
  const [items, setItems] = useState<readonly QxCircleBlockedAuthor[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    getQxCircleMutes()
      .then((response) => {
        if (alive) setItems(response.items);
      })
      .catch(() => {
        if (alive) setItems([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);
  return (
    <AuthorListSection
      title="Muted"
      lede="Authors you have muted. You can unmute from their profile."
      items={items}
      loading={loading}
      onNavigate={onNavigate}
    />
  );
}
